'use client';

import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState, type RefObject } from 'react';
import styles from '../CollaborationPage.module.css';

/** 仅用于联名页；父级以视频地址作为 key，换素材时恢复默认静音。 */
export default function CollaborationVideoSoundButton({
  videoRef,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
}) {
  const t = useTranslations('activity.campaigns.collaboration');
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const syncVolume = () => setMuted(video.muted);
    video.addEventListener('volumechange', syncVolume);
    return () => video.removeEventListener('volumechange', syncVolume);
  }, [videoRef]);

  return (
    <button
      type="button"
      className={styles.videoSoundButton}
      aria-label={t(muted ? 'unmuteVideo' : 'muteVideo')}
      onClick={(event) => {
        event.stopPropagation();
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        setMuted(video.muted);
      }}
    >
      <svg
        className={styles.videoSoundIconWeb}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {muted ? (
          <>
            <path d="M11.5488 5.41406C11.7741 5.23446 12.0822 5.19921 12.3418 5.32422C12.6012 5.44926 12.7656 5.71198 12.7656 6V18C12.7656 18.288 12.6012 18.5507 12.3418 18.6758C12.0822 18.8008 11.7741 18.7656 11.5488 18.5859L7.37695 15.2598L3 15.2617C2.80118 15.2617 2.61036 15.1825 2.46973 15.042C2.32912 14.9014 2.2501 14.7105 2.25 14.5117V9.48828C2.2502 9.07424 2.58591 8.73828 3 8.73828H7.37891L11.5488 5.41406Z" />
            <path d="M21 9.72437L18.75 11.9173M18.75 11.9173L16.5 14.1103M18.75 11.9173L21 14.1103M18.75 11.9173L16.5 9.72437" />
          </>
        ) : (
          <>
            <path d="M11.5352 5.41211C11.7607 5.23379 12.0684 5.1999 12.3271 5.3252C12.5857 5.45056 12.75 5.7126 12.75 6V18C12.75 18.2874 12.5857 18.5494 12.3271 18.6748C12.0684 18.8001 11.7607 18.7662 11.5352 18.5879L7.32617 15.2598L3 15.2607C2.80124 15.2607 2.61035 15.1825 2.46973 15.042C2.32903 14.9013 2.25 14.7097 2.25 14.5107V9.48828C2.25007 9.07412 2.58583 8.73828 3 8.73828H7.32812L11.5352 5.41211Z" />
            <path d="M19.6822 8.07935C21.2659 10.5938 21.4896 13.9404 19.9831 16.9017M16.3457 9.724C17.6825 11.2865 17.8713 13.3662 16.5997 15.2064" />
          </>
        )}
      </svg>
      <svg
        className={styles.videoSoundIconWap}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {muted ? (
          <>
            <path d="M9.70215 4.60938C9.8523 4.48972 10.0575 4.46649 10.2305 4.5498C10.4034 4.63318 10.5137 4.80801 10.5137 5V15C10.5137 15.192 10.4034 15.3668 10.2305 15.4502C10.0574 15.5335 9.85133 15.5104 9.70117 15.3906L6.19238 12.5918L2.5 12.5928C2.36742 12.5928 2.24025 12.54 2.14648 12.4463C2.05272 12.3525 2 12.2254 2 12.0928V7.90723C2 7.63108 2.22386 7.40723 2.5 7.40723H6.19238L9.70215 4.60938Z" />
            <path d="M17.5 8.10352L15.625 9.93098M15.625 9.93098L13.75 11.7584M15.625 9.93098L17.5 11.7584M15.625 9.93098L13.75 8.10352" />
          </>
        ) : (
          <>
            <path d="M10.6895 4.60742C10.8398 4.48858 11.0453 4.46636 11.2178 4.5498C11.3903 4.63334 11.5 4.80832 11.5 5V15C11.5 15.1917 11.3903 15.3667 11.2178 15.4502C11.0452 15.5337 10.8398 15.5115 10.6895 15.3926L7.14941 12.5918L3.5 12.5928C3.36744 12.5928 3.24025 12.54 3.14648 12.4463C3.05273 12.3526 3.00005 12.2253 3 12.0928V7.90723C3 7.63108 3.22386 7.40723 3.5 7.40723H7.14844L10.6895 4.60742Z" />
            <path d="M17.4025 6.73291C18.7222 8.82828 18.9086 11.6171 17.6532 14.0849M14.6221 8.10346C15.7361 9.40556 15.8934 11.1386 14.8337 12.6721" />
          </>
        )}
      </svg>
    </button>
  );
}
